import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Divider, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as API from '../../../../api/client';
import * as Network from '../../../../redux/actions/network.actions';
import * as UIAction from '../../../../redux/actions/ui.actions';

import EntityCard from '../EntityCard';
import { arrayToObject } from '@hotglue/common';

const FlowConnector = (props) => {
  const { id: flowId, name, description, filterEntity, connectionName, operation } = props;
  const dispatch = useDispatch();

  const [linked, setLinked] = useState(undefined);
  const supported = useSelector((state) => state.connectors.supported && state.connectors.supported[flowId]);

  const tenant = useSelector((state) => state.configuration.tenant_id);
  const hideFlows = useSelector((state) => state.ui.hideFlows)
  const hasLinkedConnector = useMemo(() => linked && Object.entries(linked).length > 0, [linked]);
  const linkedConnector = useSelector(state => state.connectors.linked)
  const multipleTenantLinks = useSelector(state => state.preferences.multipleTenantLinks)
  const multipleConnectors = useSelector(state => state.configuration.multipleConnectors)
  const subtenantDelimiter = useSelector(state => state.preferences.featureFlags?.subtenantDelimiter ?? '_')
  const shouldDisable = useSelector(state => state.ui.shouldDisable ?? (() => false))
  const isDisabled = useMemo(() => shouldDisable(flowId), [shouldDisable])
  const byLabel = useCallback(([k1, v1], [k2, v2]) => {
    if (v1.label < v2.label) return -1;
    if (v1.label > v2.label) return 1;
    return 0;
  }, []);

  const handleFilterEntity = useCallback(([id, entity]) => {
    if (typeof filterEntity === 'function') return filterEntity(flowId, entity.id, false)
    return true
  }, [filterEntity])

  useEffect(() => {
    const getLinkedConnectors = async () => {
      const response = await API.getLinkedConnectors(null, false, flowId, tenant)
      let linkedConnectors = response.filter(({ tenant_id: tenantId }) => tenantId === tenant);
      // If "multipleTenantLinks" is true, "linkedConnectors" is empty (i.e., no linked connector with exact match)
      // and tenant is a "root tenant", filter the response including the "sub tenants"
      if (multipleTenantLinks && linkedConnectors.length === 0 && !tenant.includes(subtenantDelimiter)) {
        linkedConnectors = response.filter(({ tenant_id: tenantId }) => (tenantId ?? '').startsWith(tenant));
      }
      setLinked(arrayToObject(linkedConnectors, 'id'));
    }

    getLinkedConnectors()
  }, [linkedConnector]);

  useEffect(() => {
    if (!supported) {
      dispatch(Network.fetchSupportedConnectors(flowId));
    }
  }, [supported]);

  if (
    linked && Object.entries(linked).filter(handleFilterEntity).length === 0 && hasLinkedConnector && !multipleConnectors
    ||
    supported && Object.entries(supported).filter(handleFilterEntity).length === 0 && !hasLinkedConnector
  )
  return <></>;

  return (
    <Box display="flex" flexDirection={'column'} gap={4}>
      {!hideFlows && <Box display="flex" flexDirection={'column'} gap={1}>
        <Typography className='hg-flow-title' variant="h2" fontWeight="700" color={isDisabled && 'text.disabled'}>{name}</Typography>
        <Typography className='hg-flow-subtitle' variant="body1" fontWeight="400" color={isDisabled ? 'text.disabled' : 'text.lightish'}>
          {description}
        </Typography>
      </Box>}
      <Box className="hg-connectors" display="flex" flexWrap="wrap" gap={2}>
        {!linked && (
          <>
            <Skeleton width={Math.floor(Math.random() * (250 - 200 + 1) + 200)} height={100} />
          </>
        )}
        {linked &&
          Object.entries(linked)
            .filter(handleFilterEntity)
            .sort(byLabel)
            .map(([key, connector], index) => {
              return (
                <EntityCard
                  key={`${key}-${flowId}-${index}`}
                  // TODO(davi): this `&& false` is placed here to avoid "multipleTenantLinks" for V2 flows
                  // because it's not tested yet. When we want to enabled it, we must remove this `&& false`
                  onClickAction={() => UIAction.openEntityConfig(connector, 'connector', flowId, operation)}
                  entity={connector}
                  entityType="connector"
                  isLinked={true}
                  flowId={flowId}
                  operation={operation}
                />
              );
            })}
        {linked &&
          (!hasLinkedConnector || multipleConnectors) &&
          supported &&
          Object.entries(supported)
            .filter(([key, value]) => linked[key] === undefined)
            .filter(handleFilterEntity)
            .sort(byLabel)
            .map(([key, connector], index) => {
              return (
                <EntityCard
                  key={`${key}-${flowId}-${index}`}
                  onClickAction={() => {
                    UIAction.selectEntityAndFlowId(connector, flowId, 'connector')
                    API.isEntityLinked(connector.id).then((oFlows) => {
                      const filteredFlows = Array.isArray(oFlows) && oFlows.filter((f) => f.id !== flowId);
                      if (filteredFlows && filteredFlows.length > 0) {
                        UIAction.openCopyEntity(connector, oFlows, flowId, 'connector', connectionName);
                      } else {
                        UIAction.link(tenant, flowId, connector.id, false, { isTarget: false, isConnection: true, multipleConnectors, connectionName })
                      }
                    })
                  }}
                  entity={connector}
                  entityType="connector"
                  flowId={flowId}
                  isLinked={false}
                  operation={operation}
                />
              );
            })}
      </Box>
      {!hideFlows && <Divider />}
    </Box >
  );
};

export default FlowConnector;
